import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { GlobalHotKeys } from 'react-hotkeys';

const noOp = () => {};

const useStyles = makeStyles(theme => ({
  root: {
    maxHeight: '10%',
    float: 'right',
    width: '35%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    '& button + button': {
      marginLeft: '0.4em'
    }
  },
  anchorStyle: {
    marginLeft: '20px',
    borderRadius: '12px',
    width: '24px',
    textAlign: 'center',
    color: 'white',
    fontSize: '21px',
    lineHeight: '21px',
    textDecoration: 'none',
    border: '1px solid black',
    backgroundColor: 'rgb(255, 152, 0)'
  }
}));

const ReviewWorkflowContent = props => {
  const classes = useStyles();
  const isCoding = props.gotoEncounterDestination === 'coding';
  const canShowButton = (isCoding) || (props.userIsAuditor && !isCoding);
  if (canShowButton)
  {
    return (
      <div className={classes.root}>
        <div>
          <button
            onClick={() => {
              props.doRedirect(props.reviewDetailsRedirectUrl());
            }}
          >
            <span>Go to Details</span>
          </button>
        </div>
      </div>
    );
  }
  else {
    return (<div className={classes.root}></div>);
  }
};

const AuditWorkflowContent = ({
  doRedirect,
  encounterId,
  isOnHold,
  removeFromHold,
  placeOnHold,
  isFinalized,
  completeEncounter,
  exitEncounter,
  encounterAuditReasons,
  isLoading
}) => {
  const classes = useStyles();

  const disableFinalize =
    (encounterAuditReasons.some(x => x.isResolved === false) || isOnHold);

    let completeEncounterShortcutFunc = (disableFinalize || isLoading) ? noOp : (eId, func) => { completeEncounter(eId, func); };

    const exitEncFunc = (isLoading) ? noOp : (a, b) => { exitEncounter(a, b); };

    const onHoldFunc = (isLoading)
      ? noOp
        : (isOnHold)
          ? (eId, func) => { removeFromHold(eId); }
            : placeOnHold;

    const keyMap = {
      COMPLETE_ENCOUNTER: ["Alt+f", "Alt+F"],
      EXIT_ENC: ["Alt+e", "Alt+E", "Alt+q", "Alt+Q"],
      PLACE_ON_HOLD: ["Alt+h", "Alt+H"]
    }

    const keyHandlers = {
      EXIT_ENC: event => {
        event.preventDefault();
        exitEncFunc(encounterId, doRedirect);
      },
      COMPLETE_ENCOUNTER: event => {
        event.preventDefault();
        completeEncounterShortcutFunc(encounterId, doRedirect);
      },
      PLACE_ON_HOLD: event => {
        event.preventDefault();
        onHoldFunc(encounterId, doRedirect);
      }
    };

  return (
    <GlobalHotKeys keyMap={keyMap} handlers={keyHandlers} allowChanges={true}>
      <div className={classes.root}>
        {/* Exit Button */}
        <button
          onClick={() => exitEncounter(encounterId, doRedirect)}
        >
          <span>Exit</span>
        </button>

        {/* Hold/Remove Hold Button */}
        <button
          onClick={() =>
            isOnHold ?
              removeFromHold(encounterId) :
              placeOnHold(encounterId, doRedirect)
          }
        >
          <span>{ isOnHold ? 'Remove Hold' : 'Place Hold'}</span>
        </button>

        {/* Complete Button */}
        <button
          disabled={disableFinalize || isLoading}
          onClick={() =>
            completeEncounter(encounterId, doRedirect)
          }
          style={{ 'backgroundColor': '#FF9800' }}
        >
          <span>Complete</span>
        </button>
        <a title='Shortcuts' className={classes.anchorStyle} href='/Content/shortcutkeys.html' target='_blank'>?</a>
      </div>
    </GlobalHotKeys>
  );
};

AuditWorkflowContent.propTypes = {
  doRedirect: PropTypes.func.isRequired,
  encounterId: PropTypes.string.isRequired,
  isOnHold: PropTypes.bool.isRequired,
  removeFromHold: PropTypes.func.isRequired,
  placeOnHold: PropTypes.func.isRequired,
  isFinalized: PropTypes.bool.isRequired,
  completeEncounter: PropTypes.func.isRequired,
  exitEncounter: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  encounterAuditReasons: PropTypes.arrayOf(
    PropTypes.shape({
      isResolved: PropTypes.bool.isRequired
    })
  ).isRequired
};

const WorkflowSection = ({
  encounterId,
  isOnHold,
  removeFromHold,
  placeOnHold,
  isFinalized,
  finalizeEncounter,
  unfinalizeEncounter,
  exitEncounter,
  doRedirect,
  encounterAuditReasons,
  reviewDetailsRedirectUrl,
  completeEncounter,
  markEncounterAsInvalid,
  dxList,
  canRemoveHold,
  isLoading,
  userIsAuditor,
  gotoEncounterDestination,
  isReview = false,
  isAudit = false,
  allowReviewRedirect = false
}) => {

  const classes = useStyles();

  if (isReview) {
    if (allowReviewRedirect) {
      return (
        <ReviewWorkflowContent
          doRedirect={doRedirect}
          reviewDetailsRedirectUrl={reviewDetailsRedirectUrl}
          userIsAuditor={userIsAuditor}
          gotoEncounterDestination={gotoEncounterDestination}
        />
      );
    }
    return null;
  }

  if (isAudit) {
    return (
      <AuditWorkflowContent
        doRedirect={doRedirect}
        encounterId={encounterId}
        isOnHold={isOnHold}
        removeFromHold={removeFromHold}
        placeOnHold={placeOnHold}
        isFinalized={isFinalized}
        finalizeEncounter={finalizeEncounter}
        unfinalizeEncounter={unfinalizeEncounter}
        exitEncounter={exitEncounter}
        encounterAuditReasons={encounterAuditReasons}
        completeEncounter={completeEncounter}
        isLoading={isLoading}
      />
    );
  }

  const exitEncFunc = (isLoading || isReview) ? noOp : (a, b) => { exitEncounter(a, b); };

  const canFinalize = dxList.listItems.reduce((acc, curr) => {
    if (acc === false) return false;
    const isWorked = (curr.isApproved === true || curr.isRemoved === true);
    return isWorked;
  }, true);

  const finalizeFunc = (canFinalize && !isLoading && !isOnHold && !isReview) ? finalizeEncounter :  noOp;

  const onHoldFunc = ((isOnHold && !canRemoveHold) || isLoading || isReview) ? noOp : (isOnHold) ? (eId, func) => { removeFromHold(eID); } : placeOnHold;

  const keyMap = {
    FINALIZE_ENCOUNTER: ["Alt+f", "Alt+F"],
    EXIT_ENC: ["Alt+e", "Alt+E", "Alt+q", "Alt+Q"],
    PLACE_ON_HOLD: ["Alt+h", "Alt+H"]
  }

  const keyHandlers = {
    EXIT_ENC: event => {
      event.preventDefault();
      exitEncFunc(encounterId, doRedirect);
    },
    FINALIZE_ENCOUNTER: event => {
      event.preventDefault();
      finalizeFunc(encounterId, doRedirect);
    },
    PLACE_ON_HOLD: event => {
      event.preventDefault();
      onHoldFunc(encounterId, doRedirect);
    }
  };

  return (
    <GlobalHotKeys handlers={keyHandlers} keyMap={keyMap} allowChanges={true}>
    <div className={classes.root}>
      {/* Exit Button */}
        <button
          onClick={() => exitEncounter(encounterId, doRedirect)}
        >
          <span>Exit</span>
        </button>
      {/* Query Button */}
      <button
      >
        <span>Query</span>
      </button>

      {/* Mark as Invalid Button */}
      <button
        disabled={isLoading}
        onClick={() => markEncounterAsInvalid(encounterId, doRedirect)}
      >
        <span>Mark Invalid</span>
      </button>

      {/* Hold/Remove Hold Button */}
      <button
        disabled={isOnHold && !canRemoveHold || isLoading}
        onClick={() =>
          isOnHold ?
            removeFromHold(encounterId) :
            placeOnHold(encounterId, doRedirect)
        }
      >
        <span>{ isOnHold ? 'Remove Hold' : 'Place Hold'}</span>
      </button>

      {/* Finalize/Unfinalize Button */}
      <button
        disabled={(isOnHold || !canFinalize || isLoading)}
        onClick={() =>
          isFinalized ?
            unfinalizeEncounter(encounterId) :
            finalizeEncounter(encounterId, doRedirect)
        }
        style={{ 'backgroundColor': '#FF9800' }}
      >
        <span>{ isFinalized ? 'Unfinalize' : 'Finalize'}</span>
      </button>
        <a title='Shortcuts' className={classes.anchorStyle} href='/Content/shortcutkeys.html' target='_blank'>?</a>
    </div>
    </GlobalHotKeys>
  );
};

WorkflowSection.propTypes = {
  encounterId: PropTypes.oneOfType([
    PropTypes.number.isRequired,
    PropTypes.string.isRequired
  ]).isRequired,
  isOnHold: PropTypes.bool.isRequired,
  removeFromHold: PropTypes.func.isRequired,
  placeOnHold: PropTypes.func.isRequired,
  isFinalized: PropTypes.bool.isRequired,
  finalizeEncounter: PropTypes.func.isRequired,
  unfinalizeEncounter: PropTypes.func.isRequired,
  exitEncounter: PropTypes.func.isRequired,
  doRedirect: PropTypes.func.isRequired,
  isReview: PropTypes.bool.isRequired,
  isAudit: PropTypes.bool.isRequired,
  reviewDetailsRedirectUrl: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  allowReviewRedirect: PropTypes.bool,
  encounterAuditReasons: PropTypes.array.isRequired
};

export default WorkflowSection;
